import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { useGet } from '../../../sharedModules/hooks/useQuery';
import axios from '../../../sharedModules/utils/axios.utils';
import { resolveApiAssetUrl } from '../../../sharedModules/utils/apiAssetUrl';
import EquipmentImageUploadDialog, { EquipmentImageRecord } from './EquipmentImageUploadDialog';

const SECTIONS = ['hvac', 'plumbing', 'electrical', 'water_treatment', 'air_quality'] as const;
type Section = (typeof SECTIONS)[number];

const SECTION_LABELS: Record<Section, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  water_treatment: 'Water Treatment',
  air_quality: 'Air Quality',
};

const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
  split_system: 'Split System',
  boiler: 'Boiler',
  mini_split: 'Mini-Split',
  package_unit: 'Package Unit',
  tank_water_heater: 'Tank Water Heater',
  tankless_water_heater: 'Tankless Water Heater',
};

interface ImagesResponse {
  result: EquipmentImageRecord[];
  missing_defaults: string[];
}

const EquipmentImageLibrary: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<EquipmentImageRecord | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<EquipmentImageRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const activeSection = SECTIONS[activeTab];

  const { data: response, isLoading, refetch } = useGet<ImagesResponse>({
    url: '/home_services/inspection_equipment_images',
  });

  const allImages = response?.data?.result ?? [];
  const missingDefaults = response?.data?.missing_defaults ?? [];
  const sectionImages = allImages.filter((img) => img.section === activeSection);

  const handleOpenAdd = () => {
    setEditRecord(undefined);
    setDialogOpen(true);
  };

  const handleOpenEdit = (record: EquipmentImageRecord) => {
    setEditRecord(record);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/home_services/inspection_equipment_images/${deleteTarget.id}`);
      refetch();
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const labelFor = (img: EquipmentImageRecord): string => {
    const parts: string[] = [];
    if (img.equipment_type) parts.push(EQUIPMENT_TYPE_LABELS[img.equipment_type] ?? img.equipment_type);
    if (img.manufacturer_description) parts.push(img.manufacturer_description);
    if (parts.length === 0) parts.push('Section Default');
    return parts.join(' — ');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Equipment Image Library</Typography>
        <Button variant="contained" size="small" startIcon={<AddPhotoAlternateIcon />} onClick={handleOpenAdd}>
          Add Image
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure which image appears for each equipment section in the inspection report PDF. Images are matched by
        section + type + manufacturer, with automatic fallback to the built-in placeholder if no match is found.
      </Typography>

      {missingDefaults.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Missing default image for:{' '}
          <strong>{missingDefaults.map((s) => SECTION_LABELS[s as Section] ?? s).join(', ')}</strong>. The PDF will
          use the built-in placeholder image for these sections until a default is configured.
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {SECTIONS.map((section) => (
          <Tab
            key={section}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {SECTION_LABELS[section]}
                {missingDefaults.includes(section) && (
                  <Tooltip title="No default image configured">
                    <BrokenImageIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  </Tooltip>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sectionImages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <BrokenImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography>No images configured for {SECTION_LABELS[activeSection]}.</Typography>
          <Typography variant="body2">
            The PDF will use the built-in placeholder. Click <strong>Add Image</strong> to configure one.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 2,
          }}
        >
          {sectionImages.map((img) => (
            <Paper
              key={img.id}
              variant="outlined"
              sx={{ overflow: 'hidden', borderRadius: 2, position: 'relative' }}
            >
              <Box sx={{ height: 130, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {img.image_url ? (
                  <Box
                    component="img"
                    src={resolveApiAssetUrl(img.image_url) ?? ''}
                    alt={labelFor(img)}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <BrokenImageIcon sx={{ fontSize: 40, color: '#ccc' }} />
                )}
              </Box>
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3 }}>
                  {labelFor(img)}
                </Typography>
              </Box>
              <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                <Tooltip title="Edit">
                  <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)' }} onClick={() => handleOpenEdit(img)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                    onClick={() => setDeleteTarget(img)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <EquipmentImageUploadDialog
        open={dialogOpen}
        section={activeSection}
        record={editRecord}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the image for <strong>{deleteTarget ? labelFor(deleteTarget) : ''}</strong>? The PDF will fall back
            to the next matching image or the built-in placeholder.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentImageLibrary;
